import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { TranslateService } from '@ngx-translate/core';
import { catchError, Subscription, throwError } from 'rxjs';
import { SharedStateService } from '../../../shared/services/shared-state.service';
import { FIELD_NAMES, FIELD_TYPES, UNIT_TYPES } from '../../constants/app-constants';
import { FormValues, RequiredField } from '../../dto/blower-payload.dto';
import { Field } from '../../models/field.model';
import { DynamicFormBaseService } from '../../services/dynamic-form-base.service';
import { NotificationType } from '../../services/notification.constants';
import { NotificationService } from '../../services/notification.service';
import { ProductsService } from '../../services/products.service';
import { UnitConversionService } from '../../services/unit-conversion.service';

@Component({
  selector: 'app-dynamic-form-model',
  templateUrl: './dynamic-form-model.component.html',
  styleUrls: ['./dynamic-form-model.component.scss'],
  standalone: false,
})
export class DynamicFormModelComponent implements OnInit, OnChanges, OnDestroy {
  @Input() formFields!: Field[];
  @Input() tab!: string;
  @Input() models: string[] = [];
  @Input() defaultModelName!: string;
  @Output() formValueChanged = new EventEmitter<{
    formValues: FormValues;
    requiredFields: RequiredField[];
    selectedUnits: { [key: string]: string };
  }>();
  @Output() modelSelected = new EventEmitter<string>();

  dynamicForm!: FormGroup;
  // expose constants to template and reuse in code
  public FIELD_TYPES = FIELD_TYPES;
  public UNIT_TYPES = UNIT_TYPES;
  public FIELD_NAMES = FIELD_NAMES;
  showWarning: string | boolean = false;
  selectedUnits: { [key: string]: string } = {};
  convertedMinMax: { [key: string]: { min: number; max: number } } = {};
  originalMinMax: { [key: string]: { min: number; max: number } } = {};
  blowerOperatingFields: Field[] = [];
  heatRecoveryFields: Field[] = [];
  heatRecoveryUsed: boolean = true;
  private unitTypeSubscription: Subscription | undefined;
  private heatRecoverySub: Subscription | undefined;
  private formValueChangesSub: Subscription | undefined;

  constructor(
    private productService: ProductsService,
    private unitConversionservice: UnitConversionService,
    private notificationService: NotificationService,
    private formBaseService: DynamicFormBaseService,
    private sharedStateService: SharedStateService,
    private translate: TranslateService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formFields'] && !changes['formFields'].isFirstChange()) {
      this.splitFields();
      this.initializeForm();
    }
    if (changes['tab'] && !changes['tab'].isFirstChange()) {
      this.patchFormValues();
    }
  }

  ngOnInit(): void {
    this.normalizeFieldLabels();
    this.splitFields();
    this.initializeForm();
    this.heatRecoverySub = this.sharedStateService.heatRecoveryUsed$.subscribe((val) => {
      this.heatRecoveryUsed = val;
    });
  }

  private initializeForm(): void {
    this.productService.initializeModelFormValues();
    const group = this.createFormGroup();
    this.dynamicForm = new FormGroup(group);
    // subscribe to unit type radio control so changing between SI/US updates unit dropdowns
    this.subscribeToUnitTypeChanges();
    this.setupFormSubscription();
    this.patchFormValues();
    this.applyInitialCalculatedDefaults();
  }

  private applyInitialCalculatedDefaults(): void {
    // Compute now so a fresh session shows calculated defaults instead of blank fields.
    this.calculateAndSetAtmosphericPressure(this.dynamicForm.value);

    const absolutePressureAfterRecoveryControl = this.dynamicForm.get('absolutePressureAfterRecovery');
    const atmosphericPressureControl = this.dynamicForm.get(FIELD_NAMES.ATMOSPHERIC_PRESSURE);
    const hasNoValue = absolutePressureAfterRecoveryControl?.value === undefined || absolutePressureAfterRecoveryControl?.value === null;

    if (absolutePressureAfterRecoveryControl && atmosphericPressureControl && hasNoValue) {
      absolutePressureAfterRecoveryControl.setValue(atmosphericPressureControl.value, { emitEvent: false });
    }
  }

  private subscribeToUnitTypeChanges(): void {
    const ctrl = this.dynamicForm?.get(FIELD_NAMES.UNIT_TYPE);
    if (!ctrl) {
      return;
    }
    // clean up existing subscription if any
    this.unitTypeSubscription?.unsubscribe();
    this.unitTypeSubscription = ctrl.valueChanges.subscribe((val) => {
      this.handleUnitTypeChange(val);
    });
    // apply immediately so default selection is reflected
    this.handleUnitTypeChange(ctrl.value);
  }

  private handleUnitTypeChange(unitType: string): void {
    if (!this.formFields || !unitType) {
      return;
    }

    for (const field of this.formFields) {
      if (!field.units || field.units.length === 0) {
        continue;
      }

      const targetUnit = this.determineTargetUnit(field, unitType);

      // if already selected, skip
      if (this.selectedUnits[field.name] === targetUnit) {
        continue;
      }

      const control = this.dynamicForm.get(field.name);
      const currentValue = control ? parseFloat(control.value) : NaN;

      if (isNaN(currentValue)) {
        this.applyNewUnitForEmptyValue(field, targetUnit);
      } else {
        this.applyNewUnitWithValue(field, targetUnit);
      }
    }
  }

  private determineTargetUnit(field: Field, unitType: string): string {
    // Default to baseUnit when available, otherwise the first listed unit
    const defaultUnit = field.baseUnit ?? field.units[0];
    if (unitType === UNIT_TYPES.US) {
      const alt = field.units.find((u) => u !== field.baseUnit);
      return alt ?? defaultUnit;
    }
    return defaultUnit;
  }

  private applyNewUnitForEmptyValue(field: Field, targetUnit: string): void {
    // No numeric value yet: just update the selected unit and min/max validators
    this.selectedUnits[field.name] = targetUnit;
    if (field.baseUnit && field.category) {
      field.min = this.originalMinMax[field.name]?.min;
      field.max = this.originalMinMax[field.name]?.max;
      this.convertMinMax(field, field.baseUnit, targetUnit, field.category);
      this.updateValidators(field.name, field.min, field.max);
    }
  }

  private applyNewUnitWithValue(field: Field, targetUnit: string): void {
    // Numeric value exists: delegate to existing conversion logic which updates value + min/max
    const fakeEvent = { target: { value: targetUnit } } as unknown as Event;
    this.formBaseService.onUnitChange(fakeEvent, field, this.dynamicForm, {
      selectedUnits: this.selectedUnits,
      originalMinMax: this.originalMinMax,
      convertedMinMax: this.convertedMinMax,
    });
  }

  ngOnDestroy(): void {
    this.unitTypeSubscription?.unsubscribe();
    this.heatRecoverySub?.unsubscribe();
    this.formValueChangesSub?.unsubscribe();
  }

  private createFormGroup(): { [key: string]: FormControl } {
    const group: { [key: string]: FormControl } = {
      [FIELD_NAMES.SELECTED_MODEL]: new FormControl('', Validators.required),
      [FIELD_NAMES.UNIT_TYPE]: new FormControl(UNIT_TYPES.SI),
    };
    // Load any stored values for fields and populate the group. We avoid
    // returning early so the function has a single return point (fixes
    // SonarQube rule about functions returning the same value from multiple
    // branches).
    const storedFieldValues = this.productService.getFormFieldValuesModel();
    if (this.formFields && this.formFields.length > 0) {
      // Delegate per-field initialization to keep implementation compact and
      // distinct from other copies flagged by duplication tools.
      this.formFields.forEach((field: Field) => this.addFieldToGroup(field, group, storedFieldValues));
    }

    return group;
  }

  private addFieldToGroup(field: Field, group: { [key: string]: FormControl }, storedValues: { [key: string]: any }): void {
    const validators = this.formBaseService.buildValidators(field);
    const initialValue = storedValues[field.name] === undefined ? field?.value : storedValues[field.name];

    group[field.name] = new FormControl(initialValue, validators);
    this.formBaseService.initializeUnitIfNeeded(field, this.selectedUnits);
    this.formBaseService.storeOriginalMinMax(field, this.originalMinMax);
  }

  private setupFormSubscription(): void {
    this.formValueChangesSub?.unsubscribe();
    this.formValueChangesSub = this.dynamicForm.valueChanges
      .pipe(
        catchError((error) => {
          this.notificationService.showNotification(
            NotificationType.Error,
            this.translate.instant('ERROR_MESSAGES.ERROR_DURING_VALUE_CHANGES'),
          );
          return throwError(error);
        }),
      )
      .subscribe((res) => this.handleFormValueChanges(res));
  }

  private handleFormValueChanges(formValues: FormValues): void {
    // Calculate atmospheric pressure from altitude
    // and set it on the corresponding atmospheric pressure control so the
    // UI shows the value immediately (before submit).
    this.calculateAndSetAtmosphericPressure(formValues);

    this.productService.updateFormFieldValuesModel(this.dynamicForm.value);
    const requiredFields = this.getRequiredFields();
    this.formBaseService.convertFormValuesToBaseUnits(formValues, this.formFields, this.selectedUnits).then((convertedValues) => {
      this.updateFormState(formValues);
      // checkTemperatures removed; emit form changes directly
      this.showWarning = false;
      this.emitFormChanges(convertedValues, requiredFields);
    });
    this.modelSelected.emit(this.dynamicForm.get(FIELD_NAMES.SELECTED_MODEL)?.value);
  }

  /**
   * If an altitude value exists in the passed formValues, compute the
   * atmospheric pressure using the formula:
   *   atmospheric pressure = (0.0000005*alt^2 - 0.0119*alt + 101.28)*1000
   */
  private calculateAndSetAtmosphericPressure(formValues: FormValues): void {
    const altitudeMeters = this.getAltitudeMetersFromForm(formValues);
    if (altitudeMeters === null) {
      return;
    }

    const atmRounded = this.computeAtmosphericPressureRounded(altitudeMeters);

    // Set atmospheric pressure control
    this.setPressureControlIfExists(FIELD_NAMES.ATMOSPHERIC_PRESSURE, atmRounded, formValues);

    // Also compute Discharge Absolute Pressure = atmospheric pressure + 2000 (Pa)
    const dischargeValuePa = Math.round(atmRounded + 2000);
    this.setPressureControlIfExists(FIELD_NAMES.DISCHARGE_ABSOLUTE_PRESSURE, dischargeValuePa, formValues);
  }

  private getAltitudeMetersFromForm(formValues: FormValues): number | null {
    if (!formValues) {
      return null;
    }
    const altitudeRaw = (formValues as any)[FIELD_NAMES.ALTITUDE_FROM_SEA_LEVEL];
    if (altitudeRaw === null || altitudeRaw === undefined || (typeof altitudeRaw === 'string' && altitudeRaw === '')) {
      return null;
    }
    const altitudeNum = Number(altitudeRaw);
    if (Number.isNaN(altitudeNum)) {
      return null;
    }
    const altitudeUnit = this.selectedUnits[FIELD_NAMES.ALTITUDE_FROM_SEA_LEVEL];
    if (altitudeUnit && typeof altitudeUnit === 'string' && altitudeUnit.toLowerCase().includes('ft')) {
      return altitudeNum * 0.3048;
    }
    return altitudeNum;
  }

  private computeAtmosphericPressureRounded(altitudeMeters: number): number {
    const atm = (0.0000005 * altitudeMeters * altitudeMeters - 0.0119 * altitudeMeters + 101.28) * 1000;
    return Math.round(atm);
  }

  private setPressureControlIfExists(controlName: string, pressurePa: number, formValues: FormValues): void {
    const control = this.dynamicForm.get(controlName);
    if (!control) {
      return;
    }
    const unit: string | undefined = this.selectedUnits[controlName] || this.dynamicForm.get(controlName + 'Unit')?.value;
    let valueToSet = pressurePa;
    // convert Pa -> inHg if necessary (1 inHg = 3386.389 Pa)
    if (unit && typeof unit === 'string' && unit.toLowerCase().includes('inhg')) {
      valueToSet = Math.round(pressurePa / 3386.389);
    }
    control.setValue(valueToSet, { emitEvent: false });
    try {
      (formValues as any)[controlName] = valueToSet;
    } catch (e) {
      this.notificationService.showNotification(
        NotificationType.Error,
        `Error setting ${controlName} value: ` + (e instanceof Error ? e.message : e),
      );
    }
  }

  private getRequiredFields(): RequiredField[] {
    if (!this.formFields) {
      return [];
    }
    // Use reduce to build the required fields list (same result, different shape)
    return this.formFields.reduce((acc: RequiredField[], field: Field) => {
      if (field.required) {
        acc.push({
          name: field.name,
          value: this.dynamicForm.get(field.name)?.value,
          min: this.originalMinMax[field.name]?.min,
          max: this.originalMinMax[field.name]?.max,
        });
      }
      return acc;
    }, []);
  }

  private updateFormState(formValues: FormValues): void {
    // Use a compact object literal for the model state
    const state = { formValues, selectedUnits: this.selectedUnits };
    this.productService.setFormStateModel(state);
  }

  private emitFormChanges(convertedValues: FormValues, requiredFields: RequiredField[]): void {
    this.formValueChanged.emit(this.createEmitPayload(convertedValues, requiredFields));
  }

  private createEmitPayload(convertedValues: FormValues, requiredFields: RequiredField[]) {
    return {
      formValues: convertedValues,
      requiredFields,
      selectedUnits: this.selectedUnits,
    };
  }

  onUnitChange(event: Event, field: Field): void {
    // Wrap the base service call so the implementation shape is different
    this.handleUnitChange(event, field);
  }

  private handleUnitChange(event: Event, field: Field): void {
    this.formBaseService.onUnitChange(event, field, this.dynamicForm, {
      selectedUnits: this.selectedUnits,
      originalMinMax: this.originalMinMax,
      convertedMinMax: this.convertedMinMax,
    });
  }

  onInputChange(event: Event, field: Field) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    // Only convert to number when the field loses focus
    if (value && !isNaN(parseFloat(value))) {
      this.dynamicForm.get(field.name)?.setValue(parseFloat(value));
    }
  }

  private splitFields(): void {
    if (!this.formFields) {
      return;
    }
    // Ensure labels are human-friendly (convert keys like ATMOSPHERIC_PRESSURE -> Atmospheric Pressure)
    this.normalizeFieldLabels();
    this.blowerOperatingFields = this.formFields.filter(
      (field) =>
        field.name !== 'temperatureAfterRecovery' &&
        field.name !== 'relativeHumidityAfterRecovery' &&
        field.name !== 'absolutePressureAfterRecovery',
    );
    this.heatRecoveryFields = this.formFields.filter(
      (field) =>
        field.name === 'temperatureAfterRecovery' ||
        field.name === 'relativeHumidityAfterRecovery' ||
        field.name === 'absolutePressureAfterRecovery',
    );
  }

  private normalizeFieldLabels(): void {
    if (!this.formFields || this.formFields.length === 0) {
      return;
    }
    this.formFields = this.formFields.map((field) => {
      const raw = field.label || field.name || '';
      field.label = this.getReadableLabel(raw);
      return field;
    });
  }

  private getReadableLabel(raw: string): string {
    if (!raw) {
      return '';
    }
    // If label already looks human (contains lowercase or spaces), return as-is
    if (/[a-z]/.test(raw) || raw.includes(' ')) {
      // Trim and collapse spaces
      return raw.replace(/\s+/g, ' ').trim();
    }
    // Replace underscores and multiple spaces with single space
    const s = raw.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
    // Title-case the result
    return s
      .toLowerCase()
      .split(' ')
      .map((w) => (w.length > 0 ? w[0].toUpperCase() + w.slice(1) : ''))
      .join(' ');
  }

  private patchFormValues(): void {
    const savedValues = this.productService.getFormStateModel();
    const savedConditionValues = this.productService.getFormStateCondition();
    const storedFieldValues = this.productService.getFormFieldValuesModel();
    if (Object.keys(storedFieldValues).length > 0) {
      this.dynamicForm.patchValue(storedFieldValues);
    }
    this.restoreSavedValues(savedValues, savedConditionValues, storedFieldValues);
    this.formFields?.forEach((field: Field) => {
      if (this.originalMinMax[field.name] && field.baseUnit && this.selectedUnits[field.name] && field.category) {
        field.min = this.originalMinMax[field.name]?.min;
        field.max = this.originalMinMax[field.name]?.max;
        this.convertMinMax(field, field.baseUnit, this.selectedUnits[field.name], field.category);
        this.updateValidators(field.name, field.min, field.max);
      }
    });
  }

  private restoreSavedValues(savedValues: any, savedConditionValues: any, storedFieldValues: any): void {
    if (Object.keys(storedFieldValues).length > 0) {
      // already applied above
      return;
    }

    if (savedConditionValues && savedValues === null) {
      const { formValues, selectedUnits } = savedConditionValues;
      this.dynamicForm.patchValue(formValues);
      this.selectedUnits = { ...selectedUnits };
    } else if (savedValues != savedConditionValues) {
      const { formValues, selectedUnits } = savedValues;
      this.dynamicForm.patchValue(formValues);
      this.selectedUnits = { ...selectedUnits };
    }
  }

  private convertMinMax(field: Field, baseUnit: string, newUnit: string, valueType: string): void {
    this.formBaseService.convertMinMax({
      field,
      baseUnit,
      newUnit,
      valueType,
      originalMinMax: this.originalMinMax,
      convertedMinMax: this.convertedMinMax,
      form: this.dynamicForm,
    });
  }

  private updateValidators(fieldName: string, min: number, max: number): void {
    this.formBaseService.updateValidators(fieldName, min, max, this.dynamicForm);
  }
}
